import { create } from 'zustand'
import {
  apiLogin,
  apiLogout,
  apiRefresh,
  apiRegister,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  isTokenExpired,
} from '../lib/auth'
import type { User } from '../types'

interface AuthState {
  user: User | null
  loading: boolean
  initialized: boolean

  // Actions
  initialize: () => Promise<void>
  signUp: (email: string, password: string, displayName: string) => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  loading: true,
  initialized: false,

  initialize: async () => {
    try {
      const token = getAccessToken()
      const refreshToken = getRefreshToken()

      if (!token && !refreshToken) {
        set({ user: null, loading: false, initialized: true })
        return
      }

      // If access token exists and is valid, use it
      if (token && !isTokenExpired(token)) {
        // Fetch profile from API to get full user data
        const { getCurrentUser } = await import('../services/api')
        try {
          const profile = await getCurrentUser()
          set({
            user: {
              id: profile.id,
              email: profile.email,
              display_name: profile.display_name,
              avatar_url: profile.avatar_url,
              role: profile.role as User['role'],
              created_at: profile.created_at,
            },
            loading: false,
            initialized: true,
          })
          return
        } catch {
          // Token might be invalid, try refresh
        }
      }

      // Try to refresh
      const result = await apiRefresh()
      if (result) {
        set({
          user: {
            id: result.user.id,
            email: result.user.email,
            display_name: result.user.display_name,
            avatar_url: result.user.avatar_url,
            role: result.user.role as User['role'],
            created_at: '',
          },
          loading: false,
          initialized: true,
        })
      } else {
        clearTokens()
        set({ user: null, loading: false, initialized: true })
      }
    } catch (error) {
      console.error('Auth initialization error:', error)
      clearTokens()
      set({ user: null, loading: false, initialized: true })
    }
  },

  signUp: async (email: string, password: string, displayName: string) => {
    set({ loading: true })
    try {
      const result = await apiRegister(email, password, displayName)
      set({
        user: {
          id: result.user.id,
          email: result.user.email,
          display_name: result.user.display_name,
          avatar_url: result.user.avatar_url,
          role: result.user.role as User['role'],
          created_at: '',
        },
        loading: false,
      })
    } catch (error) {
      set({ loading: false })
      throw error
    }
  },

  signIn: async (email: string, password: string) => {
    set({ loading: true })
    try {
      const result = await apiLogin(email, password)
      set({
        user: {
          id: result.user.id,
          email: result.user.email,
          display_name: result.user.display_name,
          avatar_url: result.user.avatar_url,
          role: result.user.role as User['role'],
          created_at: '',
        },
        loading: false,
      })
    } catch (error) {
      set({ loading: false })
      throw error
    }
  },

  signOut: async () => {
    set({ loading: true })
    await apiLogout()
    set({ user: null, loading: false })
  },
}))
